import type { Timestamp } from 'firebase/firestore';
import type { FileType } from './library';

// ============================================
// READING PROGRESS TYPES
// ============================================

/**
 * Progress tracking for the current week's parsha in the Torah reader.
 */
export interface ParshaReadingProgress {
  ref: string; // e.g., "Exodus.13.17-17.16"
  title: string; // e.g., "Beshalach"
  hebrew?: string; // Hebrew title
  versesRead: number[]; // Verse indices marked as read
  totalVerses: number;
  completedAt?: Timestamp; // When 80%+ reached
  totalReadingTimeMs: number; // Cumulative time this parsha
  lastReadAt?: Timestamp;
  commentariesExpanded: number;
  highlightsAdded: number;
  versesFavorited: number;
}

/**
 * User's reading progress document.
 * Path: artifacts/{appId}/users/{userId}/reading/progress
 */
export interface ReadingProgress {
  // Current week's parsha progress
  currentParsha: ParshaReadingProgress;

  // Lifetime aggregates
  totalParshasCompleted: number;
  totalVersesRead: number;
  totalReadingTimeMs: number;
  totalResourcesCompleted: number;

  // Reading streaks
  currentReadingStreak: number;
  longestReadingStreak: number;
  lastReadDate: string; // ISO date (YYYY-MM-DD) for streak calc

  // Weekly stats (reset weekly with parsha change)
  weeklyReadingTimeMs: number;
  weeklyVersesRead: number;
  weeklyResourcesViewed: number;
  weeklyResourcesCompleted: number;
  weekResetParsha: string; // Parsha title when last reset

  updatedAt?: Timestamp;
}

/**
 * Progress tracking for an individual resource (PDF, video, audio, etc.)
 */
export interface ResourceProgress {
  fileId: string;
  title: string;
  type: FileType;
  viewCount: number;
  totalViewTimeMs: number;
  scrollProgress: number; // 0-100 for PDFs/docs
  mediaProgress: number; // 0-100 for video/audio
  completedAt?: Timestamp;
  lastViewedAt: Timestamp;
}

/**
 * User's resource reading progress document.
 * Path: artifacts/{appId}/users/{userId}/reading/resources
 */
export interface ResourceReadingProgress {
  resources: Record<string, ResourceProgress>;
  updatedAt?: Timestamp;
}

// ============================================
// DEFAULT VALUES
// ============================================

/**
 * Create a new empty parsha progress entry
 */
export function createEmptyParshaProgress(
  ref: string,
  title: string,
  hebrew?: string,
  totalVerses: number = 0
): ParshaReadingProgress {
  return {
    ref,
    title,
    hebrew,
    versesRead: [],
    totalVerses,
    totalReadingTimeMs: 0,
    commentariesExpanded: 0,
    highlightsAdded: 0,
    versesFavorited: 0,
  };
}

/**
 * Default reading progress for new users
 */
export const DEFAULT_READING_PROGRESS: Omit<ReadingProgress, 'updatedAt'> = {
  currentParsha: {
    ref: '',
    title: '',
    versesRead: [],
    totalVerses: 0,
    totalReadingTimeMs: 0,
    commentariesExpanded: 0,
    highlightsAdded: 0,
    versesFavorited: 0,
  },
  totalParshasCompleted: 0,
  totalVersesRead: 0,
  totalReadingTimeMs: 0,
  totalResourcesCompleted: 0,
  currentReadingStreak: 0,
  longestReadingStreak: 0,
  lastReadDate: '',
  weeklyReadingTimeMs: 0,
  weeklyVersesRead: 0,
  weeklyResourcesViewed: 0,
  weeklyResourcesCompleted: 0,
  weekResetParsha: '',
};

/**
 * Default resource reading progress for new users
 */
export const DEFAULT_RESOURCE_PROGRESS: Omit<ResourceReadingProgress, 'updatedAt'> = {
  resources: {},
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate parsha completion percentage
 */
export function calculateParshaCompletion(progress: ParshaReadingProgress): number {
  if (progress.totalVerses === 0) return 0;
  return Math.round((progress.versesRead.length / progress.totalVerses) * 100);
}

/**
 * Check if a parsha is considered complete (80%+ verses read)
 */
export function isParshaComplete(progress: ParshaReadingProgress): boolean {
  return calculateParshaCompletion(progress) >= 80;
}

/**
 * Calculate resource completion based on type
 * - PDF/Doc: time OR 80% scroll
 * - Video/Audio: 80% media progress
 */
export function isResourceComplete(
  progress: ResourceProgress,
  estimatedReadingTimeMs?: number
): boolean {
  const { type, scrollProgress, mediaProgress, totalViewTimeMs } = progress;

  switch (type) {
    case 'video':
    case 'audio':
      return mediaProgress >= 80;
    case 'pdf':
    case 'doc':
      // Complete if 80% scrolled OR viewed for estimated reading time
      if (scrollProgress >= 80) return true;
      if (estimatedReadingTimeMs && totalViewTimeMs >= estimatedReadingTimeMs) return true;
      return false;
    default:
      // For other types (images, links), just check if viewed
      return totalViewTimeMs > 0;
  }
}

/**
 * Format reading time for display
 */
export function formatReadingTime(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

/**
 * Check if reading activity qualifies for streak (2 min OR 5 verses)
 */
export function qualifiesForReadingStreak(readingTimeMs: number, versesRead: number): boolean {
  const MIN_READING_TIME_MS = 2 * 60 * 1000; // 2 minutes
  const MIN_VERSES = 5;

  return readingTimeMs >= MIN_READING_TIME_MS || versesRead >= MIN_VERSES;
}

// ============================================
// CELEBRATION MESSAGES
// ============================================

export const READING_CELEBRATIONS = {
  firstVerse: 'You read your first verse! Keep going!',
  parshaComplete: 'Mazal Tov! You completed {parsha}!',
  readingStreak: "Day {days} of reading! You're on fire!",
  badgeUnlock: 'New badge unlocked: {badge}!',
  firstParsha: 'Congratulations on completing your first parsha!',
  resourceComplete: 'You finished {resource}!',
} as const;

/**
 * Format a celebration message with variables
 */
export function formatCelebrationMessage(
  template: keyof typeof READING_CELEBRATIONS,
  variables: Record<string, string | number>
): string {
  let message = READING_CELEBRATIONS[template];
  for (const [key, value] of Object.entries(variables)) {
    message = message.replace(`{${key}}`, String(value)) as typeof message;
  }
  return message;
}
